import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { BatchLogRecordProcessor, LoggerProvider, type LogRecordProcessor } from '@opentelemetry/sdk-logs'

import { isSensitiveKey, redactValue, REDACTED_VALUE } from './sentry-redaction'
import { getTelemetryUserId } from './telemetry-user'

/**
 * 브라우저에서 logtape 로그를 OTLP로 내보내기 전에 민감한 속성을 마스킹한다.
 * Sentry 경로의 redaction과 동일한 키 규칙(sentry-redaction.ts)을 재사용한다.
 *
 * 최상위 키뿐 아니라 속성 값 안쪽까지 훑는다. logtape 는 `logger.info('msg', { request: {...} })`
 * 처럼 중첩 객체를 그대로 넘길 수 있어, 최상위만 보면 `request.headers.authorization` 같은
 * 값이 그대로 빠져나간다.
 */
class RedactionLogRecordProcessor implements LogRecordProcessor {
  onEmit(logRecord: { attributes: Record<string, unknown>; setAttribute(key: string, value: unknown): void }): void {
    for (const [key, value] of Object.entries(logRecord.attributes)) {
      if (isSensitiveKey(key)) {
        logRecord.setAttribute(key, REDACTED_VALUE)
        continue
      }

      const redacted = redactValue(value)
      if (redacted !== value) {
        logRecord.setAttribute(key, redacted)
      }
    }
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}

/**
 * 모든 로그 레코드에 현재 유저(user.id)를 부착한다. Sentry.setUser의 대체로,
 * 백엔드 span의 user.id와 같은 key를 써 Loki에서 유저별 필터·프론트백엔드 연결이 가능하다.
 */
class UserLogRecordProcessor implements LogRecordProcessor {
  onEmit(logRecord: { setAttribute(key: string, value: unknown): void }): void {
    const userId = getTelemetryUserId()
    if (userId !== undefined) {
      logRecord.setAttribute('user.id', userId)
    }
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}

interface BrowserLoggerProviderOptions {
  serviceName: string
  environment: string
  /** OTLP/HTTP logs 엔드포인트. same-origin 프록시 경로를 사용한다. */
  otlpUrl: string
}

/**
 * 브라우저용 OpenTelemetry LoggerProvider를 만든다.
 * redaction → user.id 부착 → batch export(OTLP/HTTP, fetch 기반) 순서로 처리한다.
 * exporter URL은 same-origin 프록시(`/api/telemetry/v1/logs`)이므로 CORS가 필요 없다.
 */
export function createBrowserLoggerProvider({
  serviceName,
  environment,
  otlpUrl,
}: BrowserLoggerProviderOptions): LoggerProvider {
  return new LoggerProvider({
    resource: resourceFromAttributes({
      'service.name': serviceName,
      'deployment.environment': environment,
    }),
    processors: [
      new RedactionLogRecordProcessor(),
      new UserLogRecordProcessor(),
      new BatchLogRecordProcessor(new OTLPLogExporter({ url: otlpUrl })),
    ],
  })
}
